// Picks the auth path (cookie/OTP or legacy JWT) for this page load.
//
// The choice lives in localStorage so it follows the user into new tabs. It is
// resolved once per load: the provider modules are chosen at import time, so
// re-reading per call would let another tab's switch split this tab between a
// JWT context and cookie requests.

const STORAGE_KEY = "authProvider";
const OPT_IN_PARAM = "v2";
const OPT_OUT_PARAM = "v1";

const COOKIE = "cognito";
export const LEGACY = "jwt";

const PROVIDERS = [COOKIE, LEGACY];

const DEFAULT_PROVIDER =
  process.env.REACT_APP_DEFAULT_AUTH_PROVIDER === COOKIE ? COOKIE : LEGACY;

// Storage access throws in some private modes and sandboxed frames. The URL
// flag still applies to the current load; it just will not persist.
function readStored() {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

// Storing the default would pin the user to it, so a later default flip would
// skip them. Only a choice that differs from the default is kept.
function store(provider) {
  try {
    if (provider === DEFAULT_PROVIDER) {
      window.localStorage.removeItem(STORAGE_KEY);
    } else {
      window.localStorage.setItem(STORAGE_KEY, provider);
    }
  } catch {
    // See readStored.
  }
}

// Tabs opened before the flag moved to localStorage hold it in sessionStorage.
function takeSessionFlag() {
  try {
    const value = window.sessionStorage.getItem(STORAGE_KEY);
    window.sessionStorage.removeItem(STORAGE_KEY);
    return value;
  } catch {
    return null;
  }
}

function urlWithoutFlags() {
  const url = new URL(window.location.href);
  url.searchParams.delete(OPT_IN_PARAM);
  url.searchParams.delete(OPT_OUT_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}

function resolve() {
  const params = new URLSearchParams(window.location.search);
  let requested = null;
  if (params.has(OPT_IN_PARAM)) requested = COOKIE;
  else if (params.has(OPT_OUT_PARAM)) requested = LEGACY;

  const sessionFlag = takeSessionFlag();
  if (!requested && readStored() === null && PROVIDERS.includes(sessionFlag)) {
    requested = sessionFlag;
  }

  if (requested) {
    store(requested);
    // A flag left in the address bar would re-apply on every reload and in
    // bookmarks, undoing a later switch from the banner.
    if (params.has(OPT_IN_PARAM) || params.has(OPT_OUT_PARAM)) {
      window.history.replaceState(window.history.state, "", urlWithoutFlags());
    }
    return requested;
  }

  const stored = readStored();
  return PROVIDERS.includes(stored) ? stored : DEFAULT_PROVIDER;
}

const provider = resolve();

export const isCookieAuth = () => provider === COOKIE;

// The provider is fixed per load, so switching needs a full navigation.
export function switchAuthProvider(next) {
  if (!PROVIDERS.includes(next)) {
    throw new Error(`Unknown auth provider: ${next}`);
  }
  store(next);
  window.location.assign(urlWithoutFlags());
}
